import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Calculator, 
  Search, 
  Plus, 
  Download,
  Calendar,
  TrendingUp,
  TrendingDown,
  CreditCard,
  DollarSign,
  X,
  Save,
  ArrowUpCircle,
  ArrowDownCircle
} from 'lucide-react';

const Accounting = () => {
  const [movements, setMovements] = useState([
    {
      id: 1,
      date: '2025-01-15',
      type: 'income',
      method: 'cash',
      concept: 'Venta de vinos',
      category: 'Ventas',
      amount: 1700,
      notes: 'Venta directa en bodega'
    },
    {
      id: 2,
      date: '2025-01-16',
      type: 'income',
      method: 'transfer',
      concept: 'Venta productos varios',
      category: 'Ventas',
      amount: 2740,
      notes: 'Transferencia banco BROU'
    },
    {
      id: 3,
      date: '2025-01-16',
      type: 'expense',
      method: 'transfer',
      concept: 'Pago a proveedor',
      category: 'Proveedores',
      amount: 4500,
      notes: 'Viñedos del Sur - Factura 1234'
    },
    {
      id: 4,
      date: '2025-01-17',
      type: 'expense',
      method: 'cash',
      concept: 'Gastos operativos',
      category: 'Operativos',
      amount: 800,
      notes: 'Materiales de limpieza'
    },
    {
      id: 5,
      date: '2025-01-17',
      type: 'income',
      method: 'cash',
      concept: 'Venta aceite de oliva',
      category: 'Ventas',
      amount: 900,
      notes: 'Venta al por mayor'
    }
  ]);

  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterMethod, setFilterMethod] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    type: 'income',
    method: 'cash',
    concept: '',
    category: '',
    amount: '',
    notes: ''
  });

  const categories = {
    income: ['Ventas', 'Servicios', 'Otros Ingresos'],
    expense: ['Proveedores', 'Operativos', 'Salarios', 'Servicios', 'Impuestos', 'Otros Gastos']
  };

  const filteredMovements = movements.filter(mov => {
    const matchesSearch = mov.concept.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         mov.category.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || mov.type === filterType;
    const matchesMethod = filterMethod === 'all' || mov.method === filterMethod;
    const matchesStartDate = !startDate || mov.date >= startDate;
    const matchesEndDate = !endDate || mov.date <= endDate;
    
    return matchesSearch && matchesType && matchesMethod && matchesStartDate && matchesEndDate;
  });

  // Cálculos
  const totalIncome = filteredMovements
    .filter(m => m.type === 'income')
    .reduce((sum, m) => sum + m.amount, 0);
  
  const totalExpenses = filteredMovements
    .filter(m => m.type === 'expense')
    .reduce((sum, m) => sum + m.amount, 0);
  
  const netBalance = totalIncome - totalExpenses;

  const handleAddMovement = () => {
    if (!formData.concept || !formData.category || !formData.amount) {
      alert('Por favor complete los campos obligatorios');
      return;
    }

    const newMovement = {
      id: movements.length + 1,
      ...formData,
      amount: parseFloat(formData.amount)
    };

    setMovements([newMovement, ...movements]);
    setShowAddModal(false);
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      date: new Date().toISOString().split('T')[0],
      type: 'income',
      method: 'cash',
      concept: '',
      category: '',
      amount: '',
      notes: ''
    });
  };

  const handleExport = () => {
    alert('Exportando registros contables a CSV (simulado)');
  };

  const handleGenerateReport = () => {
    alert('Generando resumen por periodo (simulado)');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
            <Calculator className="h-8 w-8" style={{color: '#D4AF37'}} />
            Contabilidad
          </h1>
          <p className="text-gray-600 mt-1">Movimientos de caja y transferencias bancarias</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleGenerateReport}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Calendar className="h-5 w-5" />
            Resumen Periodo
          </button>
          <button
            onClick={handleExport}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <Download className="h-5 w-5" />
            Exportar CSV
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-6 py-2 text-white rounded-lg hover:opacity-90 transition-opacity shadow-lg"
            style={{background: 'linear-gradient(135deg, #D4AF37 0%, #C9A961 100%)'}}
          >
            <Plus className="h-5 w-5" />
            Nuevo Movimiento
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-600">Total Ingresos</h3>
              <p className="text-3xl font-bold mt-2 text-green-600">
                USD {totalIncome.toLocaleString()}
              </p>
            </div>
            <div className="p-3 bg-green-100 rounded-full">
              <TrendingUp className="h-8 w-8 text-green-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-600">Total Egresos</h3>
              <p className="text-3xl font-bold mt-2 text-red-600">
                USD {totalExpenses.toLocaleString()}
              </p>
            </div>
            <div className="p-3 bg-red-100 rounded-full">
              <TrendingDown className="h-8 w-8 text-red-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-600">Saldo Neto</h3>
              <p className={`text-3xl font-bold mt-2 ${netBalance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                USD {netBalance.toLocaleString()}
              </p>
            </div>
            <div className="p-3 rounded-full" style={{background: 'rgba(212, 175, 55, 0.1)'}}>
              <DollarSign className="h-8 w-8" style={{color: '#D4AF37'}} />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="grid md:grid-cols-5 gap-4">
          <div className="relative md:col-span-2">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por concepto o categoría..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
            />
          </div>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
          >
            <option value="all">Todos los tipos</option>
            <option value="income">Ingresos</option>
            <option value="expense">Egresos</option>
          </select>
          <select
            value={filterMethod}
            onChange={(e) => setFilterMethod(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
          >
            <option value="all">Todos los métodos</option>
            <option value="cash">Efectivo</option>
            <option value="transfer">Transferencia</option>
          </select>
          <div className="flex gap-2">
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-sm"
              placeholder="Desde"
            />
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-sm"
              placeholder="Hasta"
            />
          </div>
        </div>
      </div>

      {/* Movements Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead style={{background: 'linear-gradient(135deg, #D4AF37 0%, #C9A961 100%)'}}>
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-white">Fecha</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-white">Tipo</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-white">Método</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-white">Concepto</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-white">Categoría</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-white">Importe</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-white">Notas</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredMovements.map((movement) => (
                <tr key={movement.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 text-sm text-gray-700">{movement.date}</td>
                  <td className="px-6 py-4">
                    {movement.type === 'income' ? (
                      <span className="flex items-center gap-2 text-green-600">
                        <ArrowUpCircle className="h-5 w-5" />
                        <span className="font-medium">Ingreso</span>
                      </span>
                    ) : (
                      <span className="flex items-center gap-2 text-red-600">
                        <ArrowDownCircle className="h-5 w-5" />
                        <span className="font-medium">Egreso</span>
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 text-xs rounded-full ${
                      movement.method === 'cash'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-blue-100 text-blue-700'
                    }`}>
                      {movement.method === 'cash' ? 'Efectivo' : 'Transferencia'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm font-medium text-gray-800">{movement.concept}</td>
                  <td className="px-6 py-4">
                    <span className="px-3 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
                      {movement.category}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`text-lg font-bold ${
                      movement.type === 'income' ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {movement.type === 'income' ? '+' : '-'} USD {movement.amount.toLocaleString()}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">{movement.notes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Movement Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-2xl font-bold text-gray-800">Nuevo Movimiento</h3>
              <button onClick={() => { setShowAddModal(false); resetForm(); }}>
                <X className="h-6 w-6 text-gray-500 hover:text-gray-700" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fecha <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({...formData, date: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({...formData, type: e.target.value, category: ''})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  >
                    <option value="income">Ingreso</option>
                    <option value="expense">Egreso</option>
                  </select>
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Método de Pago <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.method}
                    onChange={(e) => setFormData({...formData, method: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  >
                    <option value="cash">Efectivo (Caja)</option>
                    <option value="transfer">Transferencia Bancaria</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Categoría <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  >
                    <option value="">Seleccionar categoría</option>
                    {categories[formData.type].map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Concepto <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.concept}
                  onChange={(e) => setFormData({...formData, concept: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  placeholder="Descripción del movimiento"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Importe (USD) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={formData.amount}
                  onChange={(e) => setFormData({...formData, amount: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  placeholder="0.00"
                  step="0.01"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notas</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  rows="3"
                  placeholder="Información adicional (opcional)"
                />
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => { setShowAddModal(false); resetForm(); }}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleAddMovement}
                className="px-6 py-2 text-white rounded-lg hover:opacity-90 transition-opacity flex items-center gap-2"
                style={{background: 'linear-gradient(135deg, #D4AF37 0%, #C9A961 100%)'}}
              >
                <Save className="h-5 w-5" />
                Guardar Movimiento
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Accounting;
